import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import type {
  ChangePasswordBody,
  DashboardPersonaBody,
  SendEmailOtpBody,
  SendOtpBody,
  UpdateUserBody,
  ValidateOtpBody,
} from 'src/openapi';
import { getApiErrorMessage, getFormattedErr } from 'src/helpers';
import { PHONE_TAKEN_MESSAGE } from 'src/constants';
import apiClient from 'src/helpers/apiClient';
import { removeWebPushSubscriptionOnLogout } from 'src/helpers/webPush';
import { isAdminMode } from 'src/helpers/storedToken';
import { announceWebAccount } from 'src/helpers/extensionBridge';
import {
  getStoredSourceUrl,
  getStoredReferrerUrl,
  getStoredReferralHash,
  getSessionSourceUrl,
  getSessionReferrerUrl,
} from 'src/helpers/utmCapture';
import { identifyUser, resetUser } from 'src/helpers/analytics';
import { LS_KEY_ANON_ID } from 'src/constants/localStorageKeys';
import { EarnCreditGoalKey } from 'src/pages/applicantDashboard/constants';
import { invalidateEarnCreditsGoalIfClaimable } from 'src/redux/services/applicantDashboard';
import type { RootState } from '..';

// Interim shape until the OpenAPI client is regenerated: the backend's
// AuthBodySchema accepts these three, the generated `AuthBody` type does not
// list them yet.
type AuthBody = Parameters<
  Awaited<ReturnType<typeof apiClient.getTokenApi>>['tokenApiV2Auth']
>[0]['authBody'] & {
  source_url?: string;
  referrer_url?: string;
  login_page_url?: string;
};

const loginSourceFields = () => ({
  source_url: getSessionSourceUrl(),
  referrer_url: getSessionReferrerUrl() || undefined,
  login_page_url: window.location.href,
});

export const login = createAsyncThunk(
  'login',
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      // Hits `/v2/auth` directly (not the generated OpenAPI client) — the v2
      // endpoint returns `{access_token, refresh_token, user, success}` and
      // sets an HttpOnly refresh cookie on `/v2/token/refresh` path. v1
      // `/auth` is reserved for mobile and returns the legacy `{token, ...}`
      // shape; we don't use it on web.
      const res = await (
        await apiClient.getTokenApi()
      ).tokenApiV2Auth({
        authBody: {
          email,
          password,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Link an existing-user login to their experiment exposure (the anon_id
          // was set by landing on the same origin). Read-only — never generated here.
          anon_id: localStorage.getItem(LS_KEY_ANON_ID) || undefined,
          // This visit's entry point, not the first-touch signup one: the
          // backend records it as the login source. Cast until the client is
          // regenerated against the new AuthBodySchema.
          ...loginSourceFields(),
        } as AuthBody,
      });
      identifyUser(res.data?.user);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

/**
 * Whether an address has an account here and signs in with Google, for the
 * sign-in screen's "Continue as" card. Silent on failure: the card falls back
 * to the password form.
 */
export const lookupAccountByEmail = createAsyncThunk(
  'auth/lookupAccountByEmail',
  async (email: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetUserDetails({ email });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

/**
 * A sign-in handed over by the Chrome extension: a one-time code minted from
 * its own session, redeemed on `/extension/signin`. Same response and cookies
 * as a password login.
 */
export const redeemLoginHandoff = createAsyncThunk(
  'redeemLoginHandoff',
  async (code: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getTokenApi()
      ).tokenApiRedeemWebHandoff({
        redeemWebHandoffBody: {
          code,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          anon_id: localStorage.getItem(LS_KEY_ANON_ID) || undefined,
          ...loginSourceFields(),
        },
      });
      identifyUser(res.data?.user);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

/**
 * A one-time code that signs the Chrome extension in as this user, bound to
 * the extension's PKCE challenge. The API also reads this browser's refresh
 * cookie, which it only receives on paths under `/v2/token/refresh`.
 */
export const createExtensionSignInCode = createAsyncThunk(
  'createExtensionSignInCode',
  async (codeChallenge: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getTokenApi()
      ).tokenApiCreateExtensionCode({
        createExtensionCodeBody: { code_challenge: codeChallenge },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

/**
 * A sign-in from a marketing-email link, redeemed on `/email/signin`. The
 * token works once. `redirectTo` is the email's own link, so its UTM tags are
 * what the login is attributed to.
 */
export const redeemEmailSignin = createAsyncThunk(
  'redeemEmailSignin',
  async (
    { token, redirectTo }: { token: string; redirectTo: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getTokenApi()
      ).tokenApiRedeemEmailSignin({
        redeemEmailSigninBody: {
          token,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          anon_id: localStorage.getItem(LS_KEY_ANON_ID) || undefined,
          ...loginSourceFields(),
          source_url: `${window.location.origin}${redirectTo}`,
        },
      });
      identifyUser(res.data?.user);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const logout = createAsyncThunk(
  '/logout',
  async ({ token }: { token: string }, { rejectWithValue }) => {
    // Before the token is revoked: on a shared machine the next person to sign
    // in must not receive this account's notifications. In Debug Mode the
    // browser's subscription is the admin's, not this learner's.
    if (!isAdminMode()) await removeWebPushSubscriptionOnLogout();
    try {
      const response = await (
        await apiClient.getTokenApi()
      ).tokenApiLogoutUser({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      resetUser();
      // Before the navigation away that follows: the extension must stop
      // offering "Continue as" this session.
      if (!isAdminMode()) announceWebAccount(null);
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const signup = createAsyncThunk(
  'signup',
  async (
    {
      email,
      password,
      emailVerificationOtp,
      requested_user_type,
    }: {
      email: string;
      password: string;
      emailVerificationOtp: string;
      requested_user_type: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiCreateUser({
        createUserBody: {
          email,
          password,
          email_verification_otp: emailVerificationOtp,
          requested_user_type,
          signup_platform_type: 'Web',
          referred_by_hash: getStoredReferralHash() || undefined,
          source_url: getStoredSourceUrl(),
          referrer_url: getStoredReferrerUrl() || undefined,
          signup_page_url: window.location.href,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const sendOtpToEmail = createAsyncThunk(
  'send_otp',
  async (data: SendEmailOtpBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiEmailActivation({ sendEmailOtpBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const verifyEmailOtp = createAsyncThunk(
  'validate_otp',
  async (
    { email, otp }: { email: string; otp: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiVerifyEmailOtp({ verifyEmailOtpBody: { email, otp } });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const sendOtpToPhone = createAsyncThunk(
  'sms/otp',
  async (data: SendOtpBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSmsApi()
      ).smsApiGenerateSms({ sendOtpBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const verifyPhoneOtp = createAsyncThunk(
  'sms/validate_otp',
  async (data: ValidateOtpBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSmsApi()
      ).smsApiValidateOtp({ validateOtpBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const userSelf = createAsyncThunk(
  'user_self',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserSelf();
      identifyUser(response.data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  },
);

export const sentOtpToResetPassword = createAsyncThunk(
  'reset/sent_otp',
  async (data: SendEmailOtpBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiSendPasswordResetOtp({ sendEmailOtpBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const resetUserPassword = createAsyncThunk(
  'reset_user_password',
  async ({ obj }: { obj: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiResetUserPassword({ resetPasswordBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchUserProfileByEmail = createAsyncThunk(
  'fetch-profile-by-email',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserDetails({ email });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * phone_number_already_exists is the one rejection reachable from the
 * onboarding step, where the number is mandatory — so say what to do next
 * rather than restating the key.
 */
const PROFILE_ERROR_COPY: Record<string, string> = {
  phone_number_already_exists: PHONE_TAKEN_MESSAGE,
};

export const updateSelf = createAsyncThunk(
  'put/update-self',
  async (payload: UpdateUserBody, { rejectWithValue, dispatch }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateUserProfile({ updateUserBody: payload });
      // The profile-picture bonus is granted by this endpoint, not by the upload
      // (see _maybe_reward_profile_picture in update_user_ctrl), so the goals
      // checklist only goes stale here — and only when a picture was sent.
      if (payload.picture) {
        dispatch(
          invalidateEarnCreditsGoalIfClaimable(
            EarnCreditGoalKey.ProfilePicture,
          ),
        );
      }
      return response.data;
    } catch (err: any) {
      // Always toast. Only the candidate onboarding step also puts this on the
      // field; the other eleven callers — employer onboarding, employer profile,
      // the settings form — just `return` on reject, and updateSelf.rejected has
      // no reducer anywhere, so suppressing here left those screens silent.
      // One screen saying it twice is the cheaper fault.
      enqueueSnackbar(getApiErrorMessage(err, PROFILE_ERROR_COPY));
      return rejectWithValue(err.response.data);
    }
  },
);

// Send `goal` from the questionnaire (the server picks the persona) or
// `persona` from settings, never both.
export const setDashboardPersona = createAsyncThunk(
  'user/dashboard-persona',
  async (body: DashboardPersonaBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiSetDashboardPersona({ dashboardPersonaBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const uploadProfilePicture = createAsyncThunk(
  'upload/profile-picture',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUploadUserProfilePicture({ picture: file });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateUserType = createAsyncThunk(
  'update/user-type',
  async (userType: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiChangeUserType({ userType });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const putChangePassword = createAsyncThunk(
  'user/change-password',
  async (data: ChangePasswordBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiChangeUserPassword({ changePasswordBody: data });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/** Re-fetches the current user's credits and reports whether they changed --
 * callers that need to react to a change (e.g. showing a coin animation)
 * check `changed` themselves rather than this thunk assuming any one caller's
 * UI. */
export const refreshUserCredits = createAsyncThunk(
  'auth/refreshUserCredits',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserSelf();
      const credits = response.data?.credits || 0;
      const changed = state?.auth?.user?.credits !== response?.data?.credits;
      return { credits, changed };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteMyAccount = createAsyncThunk(
  'user/delete-account',
  async (
    data: { otp: string; phone_number?: string; email?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteMyUser({ deleteUserBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);
